/**
 * `request_user_data` / `save_user_data` — the app-side mirror of the local
 * save.
 *
 * ❓ PLACEHOLDER — not in the decomp. The shipped game persists through
 * `SaveService.SaveLocalAsync` into a file on the device and has no server leg
 * at all; the wrapper app adds one, and this is the whole of it.
 *
 * ⚠️ **Anchored to `Web Bridge Hướng dẫn.md`, not to `WebBridge.md`.** Two
 * protocol documents ship side by side in `reference/Rule&Template/` and they
 * are different revisions of the same thing, neither a superset of the other:
 * `WebBridge.md` carries the IAP section and **no `user_data` at all**
 * (`grep -c user_data` → 0), the guide carries the cloud save and no IAP. So
 * the real protocol is their union, and quoting the wrong file for this feature
 * would be quoting a revision that has never heard of it. Frozen in
 * `tests/cloudSave.test.ts`, which fails if the two are ever unified.
 *
 * The wire is deliberately dumb: `data` is a **string** in both directions and
 * the app never looks inside it (guide §3, "app không đụng vào nội dung"). So
 * the schema below is entirely the port's business, and the app cannot be
 * blamed for a field it never agreed to.
 *
 * ## What goes up, and the one deviation from rule_LOGLEVEL §5
 *
 * rule_LOGLEVEL.md §5 states the house rule: *wallet = server data, level +
 * analytics counters = local*. This port sends the level anyway, and the reason
 * is that the rule's premise does not hold here. DiceFlow keeps the ladder
 * local because its server payload already carries a wallet, boosters and
 * lives — things worth restoring on a new device. This game has **no wallet at
 * all**: `LocalSaveData` is three fields (`__0x20da2fc.c`), and the ladder step
 * is the only progress a player can lose. A cloud save that honoured the rule
 * literally would be a cloud save of nothing.
 *
 * The analytics counters stay local, as the rule says — `LevelStats` in
 * `levelLog.ts` has its own key and no path into this payload.
 *
 * ## Merge, and why it is not "newest wins"
 *
 * There is no clock on the wire — no `savedAt`, no version — so "newest" is not
 * a question the payload can answer, and inventing a timestamp would only move
 * the guess to whichever device has the worse clock. `mergeSave` therefore
 * takes the **higher ladder step**, which is monotone and cannot lose progress
 * in either direction:
 *
 *   reinstall      local is a fresh default (step 1), cloud has 25 → cloud wins
 *   offline run    local reached 30, cloud is stale at 25          → local wins
 *
 * Settings are different: `true` is the ctor default for both switches, so
 * "sound off" is the only state a player can have deliberately chosen, and the
 * merge must not let a default overwrite it. A cloud switch is applied only
 * when this device has no save file of its own — that is the reinstall case,
 * and it is the only one where the local value carries no intent.
 */

import { nativeBridge, type NativeBridge } from './bridge'
import { type LocalSaveData } from '../ui/localSave'

/**
 * How long to wait for `user_data` before starting the game anyway.
 *
 * ❓ Product decision. It is a *boot* wait — the player is looking at
 * `#bootCover` for the whole of it — so it is short by design. DiceFlow uses
 * 5 s for the same call; this build already spends its boot budget on meshes,
 * and a save that arrives after the timeout is not lost, only ignored for this
 * session (the local file is still authoritative and the next `push` re-sends).
 */
export const CLOUD_SAVE_TIMEOUT_MS = 4000

/** What this port puts in the string. Nothing else reads it. */
export interface CloudSavePayload {
  sound: boolean
  haptic: boolean
  level: number
}

export function toPayload(data: LocalSaveData): CloudSavePayload {
  return {
    sound: data.isSoundEnabled,
    haptic: data.isHapticEnabled,
    level: data.displayLevel,
  }
}

/**
 * Parse the app's string. Every field is optional and independently validated —
 * a payload written by an older build of this port must not be thrown away
 * whole because it is missing a field that was added later.
 */
export function parsePayload(raw: unknown): Partial<CloudSavePayload> {
  if (typeof raw !== 'string' || raw === '') return {}
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return {}
  }
  if (typeof parsed !== 'object' || parsed === null) return {}
  const source = parsed as Record<string, unknown>
  const out: Partial<CloudSavePayload> = {}
  if (typeof source.sound === 'boolean') out.sound = source.sound
  if (typeof source.haptic === 'boolean') out.haptic = source.haptic
  // Same floor as `LocalSave.read`: a step below 1 is not a value the original
  // can produce, so it is corruption rather than a very early save.
  if (Number.isInteger(source.level) && (source.level as number) >= 1) {
    out.level = source.level as number
  }
  return out
}

/**
 * Fold the app's copy into this device's.
 *
 * `hasLocalFile` is what separates a reinstall from an ordinary session, and it
 * is the *only* thing that lets a cloud switch through. See the header.
 */
export function mergeSave(
  local: LocalSaveData,
  cloud: Partial<CloudSavePayload>,
  hasLocalFile: boolean,
): LocalSaveData {
  const merged: LocalSaveData = { ...local }
  // Monotone in both directions — this line is why the merge cannot lose a
  // level, whichever side is stale.
  if (cloud.level !== undefined) merged.displayLevel = Math.max(local.displayLevel, cloud.level)
  if (!hasLocalFile) {
    if (cloud.sound !== undefined) merged.isSoundEnabled = cloud.sound
    if (cloud.haptic !== undefined) merged.isHapticEnabled = cloud.haptic
  }
  return merged
}

/** The part of `LocalSave` this talks to, so a test needs no `localStorage`. */
export interface CloudSaveTarget {
  readonly data: LocalSaveData
  readonly hasStoredFile: boolean
  applyCloud(data: LocalSaveData): void
}

export class CloudSave {
  constructor(
    private readonly bridge: NativeBridge = nativeBridge,
    private readonly timeoutMs = CLOUD_SAVE_TIMEOUT_MS,
    private readonly delay = (ms: number, fn: () => void) => {
      setTimeout(fn, ms)
    },
  ) {}

  /**
   * Ask the app for the save and fold it in. Resolves either way — a boot must
   * never hang on a wrapper that does not answer, and a plain browser answers
   * `''` immediately through the bridge's offline path.
   *
   * Returns what was actually applied, so a caller can log it.
   */
  async hydrate(target: CloudSaveTarget): Promise<Partial<CloudSavePayload>> {
    const reply = await this.requestWithTimeout()
    const cloud = parsePayload(reply)
    if (Object.keys(cloud).length === 0) return {}
    const merged = mergeSave(target.data, cloud, target.hasStoredFile)
    target.applyCloud(merged)
    return cloud
  }

  private requestWithTimeout(): Promise<unknown> {
    return new Promise((resolve) => {
      let settled = false
      const finish = (value: unknown): void => {
        if (settled) return
        settled = true
        resolve(value)
      }
      // `request` resolves `{ data: '' }` by itself in a plain browser, so the
      // timer only ever fires against a real, silent wrapper.
      void this.bridge
        .request('request_user_data', 'load', {}, { data: '' })
        .then((message) => finish(message.data))
      this.delay(this.timeoutMs, () => {
        if (!settled) console.warn('[cloud save] no user_data within the boot wait; using local')
        finish('')
      })
    })
  }

  /**
   * Send the current save up. Fire and forget from the caller's side — every
   * call site is a settings toggle or a level win, and neither may wait on a
   * network round trip before the UI moves.
   *
   * ⚠️ **Every call sends. There is deliberately no in-flight guard**, and the
   * absence is the fix for a measured bug rather than an omission.
   *
   * This used to coalesce: a `pushing` flag plus a `queued` payload, so a win
   * followed by a settings toggle in the same second sent once. The flag was
   * cleared only in the `.finally` of `NativeBridge.request`, which **never
   * rejects and has no timeout on the native path** — its promise settles only
   * when `receive()` is handed back the matching `callbackId`. So a single
   * unanswered `user_data_saved` left `pushing` stuck true and every later save
   * sat in `queued` for the rest of the session, silently: the `ok !== true`
   * warning lives in the `.then`, which a missing reply never reaches, and the
   * local file keeps working so nothing on screen changes.
   *
   * It could not be reproduced in a browser either — `isNative` is false there,
   * so `request` answers itself through the offline path — which left the bug
   * alive only in the environment that ships. Measured A/B against a stubbed
   * wrapper: with `user_data_saved` answered, the level-2 save goes out on the
   * win; with it unanswered, nothing is sent again for the whole session.
   *
   * DiceFlow's `save_user_data` (`Vw`) has no such flag and cannot wedge, so
   * this is a return to the reference shape. The cost is the coalescing: two
   * pushes in the same second now put two messages on the wire. That is what
   * DiceFlow does, the payload is three fields, and a duplicate save is
   * harmless where a lost one is not.
   */
  push(data: LocalSaveData): void {
    void this.bridge
      .request(
        'save_user_data',
        'save',
        { data: JSON.stringify(toPayload(data)) },
        { ok: true },
      )
      .then((reply) => {
        // WebBridge.md §2: `ok=false` means the app could not store it. Nothing
        // to retry against — the local file is still the truth, and the next
        // push carries the same state — so this is a log, not a recovery path.
        if (reply.ok !== true) console.warn('[cloud save] save_user_data failed')
      })
  }
}

export const cloudSave = new CloudSave()
